/**
 * Dashboard admin translations: admin (home, compliance, fulfillment, webhook, users, domains, products, migration, features)
 */

export const admin = {
  home: {
    title: 'Admin Home'
  },
  compliance: {
    title: 'NIS2 Compliance',
    subtitle: 'Manage disclosure requests, abuse cases and domain suspensions',
    fetchError: 'Failed to load compliance data',
    expired: 'expired',
    cards: {
      disclosure: {
        title: 'Disclosure Requests',
        description: 'Pending requests from authorities and lawyers'
      },
      abuse: {
        title: 'Abuse Cases',
        description: 'Reported spam, phishing and malware cases'
      },
      suspended: {
        title: 'Suspended Domains',
        description: 'Domains with active suspension'
      },
      handles: {
        title: 'Verifications',
        description: 'Contacts with pending or expired verification'
      }
    },
    info: {
      title: 'NIS2 Information',
      description: 'NIS2 Article 28 requires domain registrars to verify and disclose contact data.',
      point1: 'Disclosure requests from authorities must be processed within 72 hours',
      point2: 'Contact data must be checked for accuracy and updated if incorrect',
      point3: 'Domains may be temporarily suspended in case of abuse'
    },
    disclosure: {
      title: 'Disclosure Requests',
      subtitle: 'Requests for disclosure of domain contact data',
      create: 'New Request',
      searchPlaceholder: 'Search domain or requester...',
      empty: 'No disclosure requests',
      emptyDescription: 'There are no pending requests.',
      columns: {
        created: 'Created',
        domain: 'Domain',
        requester: 'Requester',
        deadline: 'Deadline',
        status: 'Status'
      },
      pagination: {
        showing: 'Showing {from} - {to} of {total}'
      }
    },
    abuse: {
      title: 'Abuse Cases',
      subtitle: 'Reported violations and abuse cases',
      searchPlaceholder: 'Search domain or reporter...',
      empty: 'No abuse cases',
      emptyDescription: 'There are currently no open abuse reports.',
      columns: {
        date: 'Date',
        domain: 'Domain',
        category: 'Category',
        reporter: 'Reporter',
        status: 'Status'
      },
      pagination: {
        showing: 'Showing {from} - {to} of {total}'
      },
      detail: {
        title: 'Abuse Case',
        subtitle: 'Case #{id}',
        save: 'Save',
        caseInfo: 'Case Information',
        created: 'Created on',
        category: 'Category',
        currentStatus: 'Current Status',
        domain: 'Domain',
        domainName: 'Domain Name',
        reporter: 'Reporter',
        reporterEmail: 'Email',
        reporterName: 'Name',
        description: 'Description',
        actions: 'Processing',
        newStatus: 'New Status',
        resolutionNote: 'Resolution Note',
        resolutionPlaceholder: 'Optional: Notes on processing or resolving the case...',
        resolution: 'Resolution',
        resolvedAt: 'Resolved on',
        resolvedBy: 'Processed by',
        notFound: 'Case not found',
        notFoundDescription: 'The requested abuse case could not be found.',
        backToList: 'Back to Overview'
      }
    },
    suspended: {
      title: 'Suspended Domains',
      subtitle: 'Manage domains with active suspension',
      searchPlaceholder: 'Search domain...',
      empty: 'No suspended domains',
      emptyDescription: 'There are currently no suspended domains.',
      unsuspend: 'Unsuspend',
      unsuspendSuccess: 'Domain unsuspended',
      unsuspendError: 'Error unsuspending domain',
      columns: {
        domain: 'Domain',
        suspendedAt: 'Suspended on',
        reason: 'Reason',
        suspendedBy: 'By',
        owner: 'Owner'
      }
    },
    handles: {
      title: 'Handle Revalidation',
      subtitle: 'Contacts with pending or expired verification',
      searchPlaceholder: 'Search email or name...',
      filterAll: 'All',
      filterPending: 'Pending',
      filterExpired: 'Expired',
      empty: 'No pending verifications',
      emptyDescription: 'All contacts are verified or have no deadline.',
      revalidate: 'Revalidate',
      revalidateSuccess: 'Revalidation sent',
      revalidateError: 'Revalidation error',
      days: 'days',
      expired: 'Expired',
      columns: {
        handle: 'Contact',
        type: 'Type',
        email: 'Email',
        phone: 'Phone',
        deadline: 'Deadline'
      }
    }
  },
  fulfillment: {
    title: 'Fulfillment Monitor',
    subtitle: 'Manage orders and monitor fulfillment',
    fetchError: 'Failed to load fulfillment data',
    thresholdInfo: 'Carts are considered stuck after {threshold}',
    tabs: {
      orders: 'Orders',
      stuck: 'Stuck Carts',
      tasks: 'Tasks'
    },
    orders: {
      title: 'Orders',
      subtitle: '{count} orders in total',
      loading: 'Loading orders...',
      filters: {
        status: 'Status',
        allStatuses: 'All statuses',
        accountId: 'Account ID',
        accountIdPlaceholder: 'Enter account ID...',
        cartId: 'Cart ID',
        cartIdPlaceholder: 'Enter UUID...',
        clear: 'Clear filters'
      },
      table: {
        cartId: 'Cart ID',
        customer: 'Customer',
        status: 'Status',
        updated: 'Updated',
        items: 'Items',
        itemCount: 'one item | {count} items'
      },
      status: {
        started: 'Started',
        open: 'Open',
        payment_processing: 'Payment processing',
        payment_failed: 'Payment failed',
        payment_canceled: 'Payment canceled',
        pending_fulfillment: 'Pending fulfillment',
        partial_fulfillment: 'Partially fulfilled',
        fulfillment: 'In fulfillment',
        done: 'Done',
        refunded: 'Refunded'
      },
      billingFreq: {
        monthly: 'Monthly',
        quarterly: 'Quarterly',
        'half-annually': 'Half-annually',
        annually: 'Annually',
        free: 'Free'
      },
      details: {
        items: 'Items',
        noItems: 'No items in cart',
        payment: 'Payment',
        paymentId: 'Payment ID',
        created: 'Created',
        paidAt: 'Paid at',
        refundedAt: 'Refunded at',
        refundReason: 'Refund reason',
        notRefundable: 'Refunds are only possible for paid orders'
      },
      empty: {
        title: 'No orders found',
        filtered: 'Try different filters',
        none: 'No orders yet'
      },
      pagination: {
        showing: 'Showing {from}-{to} of {total}',
        page: 'Page {page} of {pages}'
      }
    },
    cards: {
      stuck: {
        title: 'Stuck Carts',
        description: 'Shopping carts blocked in fulfillment process'
      },
      failed: {
        title: 'Failed Tasks',
        description: 'Tasks with error status'
      },
      webhook: {
        title: 'Webhook Monitor',
        description: 'Switch to Stripe webhook events'
      }
    },
    refund: {
      button: 'Refund',
      refunded: 'Refunded',
      dialogTitle: 'Refund Cart',
      dialogMessage: 'The entire cart will be refunded via Stripe and marked as refunded.',
      warning: 'The Stripe refund is issued immediately and cannot be undone.',
      reasonLabel: 'Reason',
      reasonPlaceholder: 'Reason for the refund...',
      confirm: 'Refund',
      success: 'Refund issued'
    }
  },
  webhook: {
    title: 'Webhook Monitor',
    subtitle: 'Monitor and retry Stripe webhook events',
    fetchError: 'Failed to load webhook events',
    retry: 'Retry',
    retrySuccess: 'Webhook queued for retry',
    retryError: 'Error retrying webhook',
    confirmRetry: {
      title: 'Reprocess webhook?',
      message: 'The webhook event will be queued for reprocessing.',
      confirm: 'Retry',
      cancel: 'Cancel'
    }
  },
  users: {
    title: 'User Management',
    subtitle: 'Manage all users and access rights',
    newUser: 'New User',
    loading: 'Loading users...',
    table: {
      users: 'Users',
      usersActive: '{total} users, {active} active',
      search: 'Search...',
      name: 'Name',
      email: 'Email',
      type: 'Type',
      role: 'Role',
      status: 'Status',
      actions: 'Actions',
      edit: 'Edit',
      toggleStatus: 'Toggle Status'
    },
    filters: {
      active: '{count} Active',
      inactive: '{count} Inactive',
      unverified: '{count} Unverified',
      admins: '{count} Admins',
      reseller: '{count} Reseller',
      customers: '{count} Customers'
    },
    empty: {
      noUsers: 'No users found',
      noResults: 'No users found for "{query}"',
      noResultsFilter: 'No {filter}users for "{query}"',
      noFilterResults: 'No {filter} found',
      activeUsers: 'active ',
      inactiveUsers: 'inactive ',
      unverifiedUsers: 'unverified ',
      adminUsers: 'admin ',
      resellerUsers: 'reseller ',
      customerUsers: 'customer ',
      activeFilter: 'active users',
      inactiveFilter: 'inactive users',
      unverifiedFilter: 'users with unverified email',
      adminsFilter: 'admins',
      resellersFilter: 'resellers',
      customersFilter: 'customers'
    },
    types: {
      person: 'Person',
      organization: 'Organisation'
    },
    notifications: {
      activated: '{name}: Activated',
      deactivated: '{name}: Deactivated'
    },
    syncStripe: {
      button: 'Sync Stripe',
      dialogTitle: 'Sync Stripe',
      dialogMessage: 'Synchronise payment methods and create missing subscriptions for this account?',
      success: 'Stripe sync successful',
      successDetail: '{methods} payment method(s), {subs} subscription(s) created'
    },
    impersonate: {
      button: 'Impersonate',
      dialogTitle: 'Impersonate User',
      dialogMessage: 'You will be logged in as this user and see the dashboard from their perspective.',
      warning: 'Security-sensitive actions (password, 2FA, payment methods) are not possible during impersonation.',
      confirm: 'Impersonate'
    },
    deactivate: {
      button: 'Deactivate',
      dialogTitle: 'Deactivate Account',
      dialogMessage: 'The account will be deactivated and login blocked.',
      warning: 'All active sessions are terminated immediately and the user can no longer log in.',
      reasonLabel: 'Reason',
      reasonPlaceholder: 'Reason for deactivation...',
      confirm: 'Deactivate',
      success: 'Account deactivated'
    },
    emailVerified: 'Email verified',
    pagination: {
      showing: 'Showing {from} - {to} of {total}'
    },
    edit: {
      newUser: 'New User',
      editUser: 'Edit User',
      createUser: 'Create new user',
      editUserData: 'Edit user data',
      saveAndNew: 'Save & New',
      save: 'Save',
      status: 'Status',
      active: 'Active',
      inactive: 'Inactive',
      emailVerified: 'Email verified',
      twoFactorActive: '2FA enabled',
      accountType: 'Account Type',
      accountTypeLabel: 'Account Type*',
      person: 'Person',
      organization: 'Organisation',
      role: 'Role*',
      customer: 'Customer',
      reseller: 'Reseller',
      admin: 'Admin',
      contactData: 'Contact Details',
      email: 'Email*',
      emailPlaceholder: "email{'@'}example.com",
      phone: 'Phone*',
      phonePlaceholder: '+44 123 456789',
      companyData: 'Company Details',
      personalData: 'Personal Details',
      companyName: 'Company Name*',
      companyPlaceholder: 'Company Ltd',
      firstName: 'First Name*',
      firstNameContact: 'Contact First Name',
      firstNamePlaceholder: 'John',
      lastName: 'Last Name*',
      lastNameContact: 'Contact Last Name',
      lastNamePlaceholder: 'Smith',
      vatId: 'VAT ID',
      vatIdPlaceholder: 'GB123456789',
      address: 'Address',
      street: 'Street and Number*',
      streetPlaceholder: '123 Example Street',
      addressSupplement: 'Address Line 2',
      addressSupplementPlaceholder: 'Building, floor, etc.',
      postcode: 'Postcode*',
      postcodePlaceholder: 'SW1A 1AA',
      city: 'City*',
      cityPlaceholder: 'London',
      state: 'County/State*',
      stateSelect: 'Select...',
      country: 'Country (ISO)*',
      countryPlaceholder: 'GB',
      countrySelect: 'Select country...',
      limits: 'Limits',
      maxTeamMembers: 'Max team members',
      maxSubCustomers: 'Max sub-customers',
      validation: {
        emailRequired: 'Email is required',
        emailInvalid: 'Invalid email address',
        phoneRequired: 'Phone is required',
        nameRequired: 'First name is required',
        surnameRequired: 'Last name is required',
        companyRequired: 'Company name is required',
        streetRequired: 'Address is required',
        cityRequired: 'City is required',
        postcodeRequired: 'Postcode is required',
        stateRequired: 'County/State is required',
        countryRequired: 'Country code is required',
        countryMaxLength: 'Max. 2 characters',
        validationError: 'Validation Error',
        checkFields: 'Please check the highlighted fields'
      },
      notifications: {
        created: 'User created',
        updated: 'User updated'
      }
    },
    detail: {
      back: 'Back to users',
      tabs: {
        overview: 'Overview',
        sessions: 'Sessions',
        roles: 'Roles',
        subAccounts: 'Sub-Accounts'
      },
      sessions: {
        title: 'Active Sessions',
        device: 'Device',
        location: 'Location',
        created: 'Created',
        status: 'Status',
        validated: 'Validated',
        notValidated: 'Pending OTP',
        current: 'Current',
        revoke: 'Revoke',
        revokeAll: 'Revoke All',
        revokeConfirm: 'Revoke this session?',
        revokeAllConfirm: 'Revoke all sessions for this user? They will be logged out everywhere.',
        revokeSuccess: 'Session revoked',
        revokeAllSuccess: '{count} session(s) revoked',
        empty: 'No active sessions',
        unknownLocation: 'Unknown',
        unknownDevice: 'Unknown device'
      }
    }
  },
  domains: {
    title: 'All Domains',
    subtitle: 'Manage all domains in the system',
    searchPlaceholder: 'Search domain...',
    status: {
      all: 'All',
      active: 'Active',
      pendingDelete: 'Cancelled',
      deleted: 'Deleted',
      tradeOut: 'Trade-Out',
      external: 'External',
      suspended: 'Suspended',
      transferPending: 'Transfer pending'
    },
    table: {
      domain: 'Domain',
      status: 'Status',
      account: 'Account',
      registered: 'Registered'
    },
    empty: {
      title: 'No domains found',
      description: 'No domains were found with the current filters.'
    },
    pagination: {
      showing: 'Showing {from} to {to} of {total} domains'
    },
    mailDeactivate: {
      sectionTitle: 'Admin Actions',
      title: 'Terminate Mail Hosting',
      description: 'Cancels this domain\'s mail subscription if one is running, disables all mailboxes and blocks email until the hold is released.',
      button: 'Terminate mail hosting',
      dialogTitle: 'Terminate Mail Hosting',
      dialogMessage: 'A running mail subscription for this domain is canceled, all mailboxes are disabled, and email stays blocked until the hold is released.',
      warning: 'A running Stripe subscription is canceled immediately and all mailboxes are disabled. Data is retained.',
      reasonLabel: 'Reason',
      reasonPlaceholder: 'Reason for deactivation...',
      confirm: 'Terminate',
      success: 'Mail hosting terminated',
      stripeUnavailable: 'Email is switched off and held, but the mail subscription could not be ended yet because payment processing was unreachable. The hourly run ends it.'
    },
    mailRelease: {
      title: 'Release Mail Hold',
      description: 'Lifts the admin hold. Mailboxes are switched back on only if the domain is entitled to email.',
      button: 'Release hold',
      dialogTitle: 'Release Mail Hold',
      dialogMessage: 'The admin hold on email for this domain is lifted. If the domain is entitled through hosting, the legacy plan or a running mail subscription, all mailboxes are switched back on; otherwise email stays off.',
      reasonLabel: 'Reason',
      reasonPlaceholder: 'Reason for the release...',
      confirm: 'Release hold',
      success: 'Mail hold released',
      notOnHold: 'This domain has no admin hold, so there was nothing to release.',
      reenableFailed: 'The hold is lifted, but not every mailbox could be switched on. The hourly run switches on the rest.',
      staysOff: 'The hold is lifted, but email stays switched off.',
      staysOffNoEntitlement: 'The hold is lifted, but email stays switched off because the domain is not entitled to email.',
      staysOffSuspended: 'The hold is lifted, but email stays switched off while the domain is suspended.'
    }
  },
  products: {
    title: 'Product Management',
    subtitle: 'Manage all products and prices',
    loading: 'Loading products...',
    editable: 'Products',
    editableSubtitle: 'Editable products',
    system: 'System Products',
    systemSubtitle: 'Non-editable products',
    newProduct: 'New Product',
    search: 'Search products...',
    noFilterResults: 'No products found',
    resetFilters: 'Reset Filters',
    table: {
      name: 'Name',
      price: 'Price',
      active: 'Active',
      public: 'Public',
      empty: 'No products found',
      toggleActive: 'Toggle active',
      togglePublic: 'Toggle public'
    },
    notifications: {
      activeEnabled: '{name}: Active enabled',
      activeDisabled: '{name}: Active disabled',
      publicEnabled: '{name}: Public enabled',
      publicDisabled: '{name}: Public disabled'
    },
    edit: {
      newProduct: 'New Product',
      editProduct: 'Edit Product',
      createProduct: 'Create a new product',
      loading: 'Loading product...',
      save: 'Save',
      status: 'Status',
      active: 'Active',
      activeDesc: 'Product is available',
      public: 'Public',
      publicDesc: 'Visible to everyone',
      basicInfo: 'Basic Information',
      name: 'Name',
      namePlaceholder: 'Enter product name...',
      slug: 'Slug',
      slugPlaceholder: 'product-slug',
      description: 'Description',
      descriptionPlaceholder: 'Enter product description...',
      paymentIntervals: 'Payment Intervals',
      intervals: {
        monthly: 'Monthly',
        quarterly: 'Quarterly',
        halfAnnually: 'Half-Yearly',
        annually: 'Annually'
      },
      prices: 'Prices',
      pricesSaveFirst: 'Save the product first to add prices',
      features: 'Features',
      featuresSaveFirst: 'Save the product first to add features',
      featureKey: 'Key...',
      featureValue: 'Value...',
      deleteFeature: 'Delete feature',
      addFeature: 'Add Feature',
      notifications: {
        updated: 'Product successfully updated',
        created: 'Product successfully created',
        featureDuplicate: 'Feature "{key}" already exists'
      }
    },
    prices: {
      newPrice: 'New Price',
      empty: 'No prices found',
      standard: 'Standard',
      active: 'Active',
      inactive: 'Inactive',
      modal: {
        newPrice: 'New Price',
        editPrice: 'Edit Price',
        active: 'Active',
        standard: 'Standard',
        amount: 'Price (in pence)',
        amountPlaceholder: 'e.g. 999 for £9.99',
        amountHint: 'Enter in pence, e.g. 999 = £9.99',
        currency: 'Currency',
        currencyPlaceholder: 'GBP',
        tax: 'Tax Rate',
        taxPlaceholder: 'e.g. 0.20 for 20%',
        taxHint: 'As decimal, e.g. 0.20 = 20%',
        description: 'Description',
        descriptionPlaceholder: 'Optional description...',
        cancel: 'Cancel',
        save: 'Save'
      },
      notifications: {
        saveFirst: 'Please save the product first before adding prices',
        updated: 'Price successfully updated',
        created: 'Price successfully created'
      }
    }
  },
  migration: {
    title: 'Domain Migration',
    subtitle: 'Import domains from INWX',
    loading: 'Loading domains...',
    stats: {
      available: 'Available',
      imported: 'Imported',
      selected: 'Selected'
    },
    table: {
      domain: 'Domain',
      expiryDate: 'Expiry Date',
      status: 'Status',
      actions: 'Actions',
      search: 'Search domain...',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      selectedCount: '{count} selected'
    },
    status: {
      available: 'Available',
      imported: 'Already imported',
      cannotImport: 'Cannot import'
    },
    actions: {
      validate: 'Validate',
      import: 'Import',
      importSelected: 'Import Selected',
      refresh: 'Refresh'
    },
    import: {
      title: 'Import Domain',
      titleBatch: 'Import Domains',
      subtitle: 'Configure the import',
      selectedDomains: 'Selected Domains',
      accountAssignment: 'Account Assignment',
      existingAccount: 'Assign to existing account',
      newAccount: 'Create new account',
      searchAccount: 'Search account (email or name)...',
      noAccountsFound: 'No accounts found',
      selectAccount: 'Select account',
      subAccount: {
        title: 'Sub-Account Relationship',
        enable: 'Import as sub-account',
        description: 'Creates a relationship between the target account and a parent account.',
        parentAccount: 'Parent Account',
        searchParent: 'Search parent account...',
        noAccounts: 'No accounts found',
        type: 'Relationship Type',
        billingMode: 'Billing Mode',
        billingShared: 'Shared',
        billingIndependent: 'Independent'
      }
    },
    accountForm: {
      title: 'New Account',
      email: 'Email',
      emailPlaceholder: "email{'@'}example.com",
      name: 'First Name',
      namePlaceholder: 'John',
      surname: 'Last Name',
      surnamePlaceholder: 'Smith',
      organization: 'Organisation',
      organizationPlaceholder: 'Company Ltd (optional)',
      street: 'Street and Number',
      streetPlaceholder: '123 Example Street',
      addressLine2: 'Address Line 2',
      addressLine2Placeholder: 'Building, floor, etc. (optional)',
      city: 'City',
      cityPlaceholder: 'London',
      postcode: 'Postcode',
      postcodePlaceholder: 'SW1A 1AA',
      country: 'Country (ISO)',
      countryPlaceholder: 'GB',
      state: 'County/State',
      statePlaceholder: 'Greater London (optional)',
      stateSelect: 'Select county/state',
      countrySelect: 'Select country',
      phone: 'Phone',
      phonePlaceholder: '+44 123 456789',
      vatId: 'VAT ID',
      vatIdPlaceholder: 'GB123456789 (optional)'
    },
    validation: {
      title: 'Validation',
      validating: 'Validating...',
      valid: 'Import possible',
      invalid: 'Cannot import',
      reason: 'Reason',
      existsInDb: 'Domain already in system',
      notInInwx: 'Domain not found in INWX',
      allValid: 'All domains can be imported',
      someInvalid: '{count} domain(s) cannot be imported',
      mustValidate: 'Please validate first'
    },
    results: {
      title: 'Import Result',
      subtitle: 'Import overview',
      summary: 'Summary',
      total: 'Total',
      successful: 'Successful',
      failed: 'Failed',
      domainResults: 'Domain Results',
      success: 'Successfully imported',
      error: 'Error',
      viewDomain: 'View domain',
      close: 'Close',
      importMore: 'Import more'
    },
    empty: {
      title: 'No domains available',
      subtitle: 'There are no domains available for import in INWX.'
    },
    notifications: {
      importSuccess: 'Domain successfully imported',
      importSuccessBatch: '{count} domain(s) successfully imported',
      importFailed: 'Import failed',
      validationSuccess: 'Validation successful',
      validationFailed: 'Validation failed',
      loadFailed: 'Failed to load domains'
    }
  },
  features: {
    title: 'Feature Flags',
    subtitle: 'Enable or disable features',
    newFlag: 'New Flag',
    loading: 'Loading feature flags...',
    stats: {
      flags: 'Flags',
      active: 'Active',
      inactive: 'Inactive',
      categories: 'Categories'
    },
    table: {
      title: 'Feature Flags',
      subtitle: '{count} flags in {categories} categories',
      search: 'Search...',
      expandAll: 'Expand all',
      categoryFlags: '{count} flags',
      active: 'Active',
      inactive: 'Inactive',
      edit: 'Edit',
      delete: 'Delete'
    },
    empty: {
      title: 'No Feature Flags',
      subtitle: 'Create a new flag to get started',
      noResults: 'No results',
      noResultsQuery: 'No flags found for "{query}"'
    },
    notifications: {
      enabled: '{name}: Enabled',
      disabled: '{name}: Disabled',
      deleted: '{category}.{name} has been deleted'
    },
    confirmDelete: 'Are you sure you want to delete flag "{category}.{name}"?',
    edit: {
      newFlag: 'New Flag',
      editFlag: 'Edit Flag',
      createFlag: 'Create new feature flag',
      editFlagData: 'Edit feature flag',
      saveAndNew: 'Save & New',
      save: 'Save',
      flagDetails: 'Flag Details',
      category: 'Category*',
      categoryPlaceholder: 'e.g. admin, support, tracking',
      categoryHint: 'Grouping for this flag',
      name: 'Name*',
      namePlaceholder: 'e.g. ticketForm, productManagement',
      nameHint: 'Unique identifier (camelCase recommended)',
      status: 'Status',
      enabled: 'Enabled',
      disabled: 'Disabled',
      enabledHint: 'Feature is visible/active',
      disabledHint: 'Feature is hidden/inactive',
      preview: 'Preview',
      previewCategory: 'category',
      previewName: 'name',
      dangerZone: 'Danger Zone',
      deleteFlag: 'Delete Flag',
      deleteHint: 'This action cannot be undone.',
      validation: {
        categoryRequired: 'Category is required',
        categoryFormat: 'Only letters, numbers, - and _ allowed',
        nameRequired: 'Name is required',
        nameFormat: 'Only letters, numbers, - and _ allowed',
        validationError: 'Validation Error',
        checkFields: 'Please check the highlighted fields'
      },
      notifications: {
        created: 'Flag created',
        updated: 'Flag updated',
        deleted: '{category}.{name} has been deleted'
      },
      confirmDelete: 'Are you sure you want to delete flag "{category}.{name}"?'
    }
  },
  storage: {
    title: 'Storage Buckets',
    subtitle: 'Manage all S3 storage buckets across accounts',
    searchPlaceholder: 'Search bucket name or account ID...',
    empty: 'No storage buckets',
    emptyDescription: 'There are currently no storage buckets in the system.',
    columns: {
      name: 'Name',
      region: 'Region',
      account: 'Account',
      status: 'Status',
      public: 'Public',
      storage: 'Storage',
      objects: 'Objects',
      created: 'Created'
    },
    publicYes: 'Public',
    publicNo: 'Private',
    actions: {
      suspend: 'Suspend',
      unsuspend: 'Unsuspend',
      suspendSuccess: 'Bucket suspended',
      unsuspendSuccess: 'Bucket unsuspended',
      error: 'Action failed'
    }
  },
  subAccounts: {
    title: 'Sub-Accounts',
    subtitle: 'Sub-accounts and relationships for this user',
    childrenTitle: 'Sub-Accounts (as parent)',
    parentsTitle: 'Parent Accounts (as child)',
    addRelationship: 'Add Relationship',
    deleteRelationship: 'Remove Relationship',
    noRelationships: 'No Relationships',
    noRelationshipsDescription: 'This account has no sub-account relationships.',
    confirmDelete: 'Are you sure you want to remove the relationship with {account}?',
    billingShared: 'Shared Billing',
    billingIndependent: 'Independent Billing',
    status: {
      active: 'Active',
      pending: 'Pending',
      deactivated: 'Deactivated',
      expired: 'Expired'
    },
    types: {
      team: 'Team',
      reseller: 'Reseller'
    },
    billing: {
      shared: 'Shared (Parent Account)',
      independent: 'Independent'
    },
    form: {
      childAccount: 'Child Account',
      searchAccount: 'Search account...',
      type: 'Relationship Type',
      billingMode: 'Billing Mode',
      label: 'Label',
      labelPlaceholder: 'Optional label...'
    },
    notifications: {
      created: 'Relationship Created',
      createdMessage: 'The sub-account relationship has been created successfully.',
      deleted: 'Relationship Removed',
      deletedMessage: 'The sub-account relationship has been removed.'
    }
  },
  registry: {
    title: 'Container Registry',
    subtitle: 'Manage registry accounts',
    searchPlaceholder: 'Search by slug, email, or account ID...',
    empty: 'No registry accounts',
    columns: {
      slug: 'Namespace',
      account: 'Account',
      region: 'Region',
      status: 'Status',
      storage: 'Storage',
      created: 'Created'
    },
    statuses: {
      active: 'active',
      suspended: 'suspended',
      deactivated: 'deactivated',
      quota_exceeded: 'storage full'
    },
    actions: {
      suspend: 'Suspend',
      suspendConfirm: 'Suspend this registry? Docker auth will be blocked.',
      suspendSuccess: 'Registry suspended',
      unsuspend: 'Unsuspend',
      unsuspendConfirm: 'Unsuspend this registry?',
      unsuspendSuccess: 'Registry unsuspended'
    },
    detail: {
      notFound: 'Registry not found',
      notFoundDescription: 'The requested registry configuration could not be found.',
      backToList: 'Back to list',
      registryInfo: 'Registry Info',
      accountInfo: 'Account',
      billing: 'Billing / Stripe',
      noBilling: 'No Stripe billing configured',
      usage: 'Latest Usage',
      noUsage: 'No usage data available',
      tokens: 'Access Tokens',
      noTokens: 'No access tokens',
      cleanupRules: 'Cleanup Rules',
      noCleanupRules: 'No cleanup rules configured',
      fields: {
        status: 'Status',
        region: 'Region',
        shard: 'Shard',
        slug: 'Namespace',
        storageQuota: 'Storage Quota',
        created: 'Created',
        accountEmail: 'Email',
        accountId: 'Account ID',
        stripeSubscription: 'Subscription',
        stripeStorageItem: 'Storage Item',
        stripeEgressItem: 'Egress Item',
        storageUsed: 'Storage Used',
        egressUsed: 'Egress',
        pullCount: 'Pulls',
        pushCount: 'Pushes',
        period: 'Period',
        reportedToStripe: 'Reported to Stripe'
      },
      pullOnly: 'Pull only',
      tokenColumns: {
        name: 'Name',
        prefix: 'Prefix',
        active: 'Active',
        scopes: 'Scopes',
        created: 'Created',
        lastUsed: 'Last Used',
        expires: 'Expires'
      },
      ruleColumns: {
        repository: 'Repository',
        ruleType: 'Rule Type',
        value: 'Value',
        keepPatterns: 'Keep Patterns',
        deletePatterns: 'Delete Patterns',
        enabled: 'Enabled'
      }
    }
  },
  kubernetes: {
    title: 'Kubernetes Tenants',
    subtitle: 'Manage all Kubernetes tenants and namespaces',
    searchPlaceholder: 'Search namespace or email...',
    status: {
      all: 'All',
      provisioning: 'Provisioning',
      active: 'Active',
      failed: 'Failed',
      deprovisioning: 'Deprovisioning',
      deprovisioned: 'Deprovisioned'
    },
    table: {
      namespace: 'Namespace',
      status: 'Status',
      account: 'Account',
      product: 'Package',
      provisioned: 'Provisioned',
      gitRepo: 'Git Repository'
    },
    empty: {
      title: 'No tenants found',
      description: 'No Kubernetes tenants were found with the current filters.'
    },
    pagination: {
      showing: 'Showing {from} to {to} of {total} tenants'
    },
    detail: {
      back: 'Back to tenant list',
      tabs: {
        config: 'Configuration',
        live: 'Live Data',
        usage: 'Usage',
        domains: 'Domains',
        actions: 'Actions'
      },
      config: {
        title: 'Tenant Configuration',
        namespace: 'Namespace',
        status: 'Status',
        errorMessage: 'Error Message',
        provisionedAt: 'Provisioned At',
        gitRepo: 'Git Repository',
        gitPath: 'Git Path',
        gitRevision: 'Git Revision',
        selfHeal: 'Self-Heal',
        yes: 'Yes',
        no: 'No',
        syncStatus: 'Sync Status',
        healthStatus: 'Health Status',
        spendingCap: 'Spending Cap'
      },
      live: {
        overview: 'Overview',
        workloads: 'Workloads',
        pods: 'Pods',
        events: 'Events',
        name: 'Name',
        kind: 'Kind',
        ready: 'Ready',
        age: 'Age',
        status: 'Status',
        restarts: 'Restarts',
        type: 'Type',
        reason: 'Reason',
        object: 'Object',
        message: 'Message',
        count: 'Count',
        empty: 'No live data available',
        noUsage: 'No usage data available',
        verified: 'Verified',
        pending: 'Pending',
        notFound: 'Tenant not found'
      },
      usage: {
        title: 'Current Usage',
        cpuCoreHours: 'CPU (Core Hours)',
        memoryGibHours: 'Memory (GiB Hours)',
        storageGib: 'Storage (GiB)',
        egressGib: 'Egress (GiB)',
        projectedBill: 'Projected Bill',
        cpuCents: 'CPU',
        memoryCents: 'Memory',
        storageCents: 'Storage',
        egressCents: 'Egress',
        baseFee: 'Base Fee',
        total: 'Total',
        period: 'Billing Period',
        daysElapsed: 'Days Elapsed'
      },
      domains: {
        title: 'Tenant Domains',
        domain: 'Domain',
        verified: 'Verified',
        verifiedAt: 'Verified At',
        token: 'Token',
        created: 'Created',
        empty: 'No domains',
        verify: 'Verify',
        delete: 'Delete',
        verifySuccess: 'Domain verified',
        deleteSuccess: 'Domain deleted',
        confirmDelete: 'Are you sure you want to delete domain "{domain}"?'
      },
      actions: {
        title: 'Tenant Actions',
        deprovision: 'Deprovision',
        deprovisionDesc: 'Remove tenant and all resources from the cluster.',
        deprovisionConfirm: 'Are you sure you want to deprovision this tenant? This action cannot be undone.',
        deprovisionSuccess: 'Tenant deprovisioned',
        syncUsage: 'Sync Usage',
        syncUsageDesc: 'Synchronise usage data for all tenants.',
        syncSuccess: 'Usage synchronised',
        regenerateKubeconfig: 'Regenerate Kubeconfig',
        regenerateDesc: 'Generate a new kubeconfig (the old one will be invalidated).',
        regenerateConfirm: 'Are you sure you want to regenerate the kubeconfig? The current one will be invalidated.',
        regenerateSuccess: 'Kubeconfig regenerated',
        provision: 'Provision',
        provisionDesc: 'Manually provision the tenant.',
        provisionSuccess: 'Tenant provisioned',
        error: 'Action failed'
      }
    },
    usageOverview: {
      title: 'Kubernetes Usage',
      subtitle: 'Usage overview across all tenants',
      table: {
        namespace: 'Namespace',
        account: 'Account',
        cpu: 'CPU (h)',
        memory: 'RAM (h)',
        storage: 'Storage (GiB)',
        egress: 'Egress (GiB)',
        projected: 'Projected',
        spendingCap: 'Cap'
      },
      empty: {
        title: 'No active tenants',
        description: 'There are currently no active Kubernetes tenants.'
      },
      pagination: {
        showing: 'Showing {from} to {to} of {total} tenants'
      }
    },
    domains: {
      title: 'Kubernetes Domains',
      subtitle: 'Manage all K8s domains across accounts',
      searchPlaceholder: 'Search domain...',
      filter: {
        all: 'All',
        verified: 'Verified',
        pending: 'Pending'
      },
      table: {
        domain: 'Domain',
        account: 'Account',
        verified: 'Status',
        created: 'Created'
      },
      verifiedLabel: 'Verified',
      pendingLabel: 'Pending',
      empty: {
        title: 'No domains found',
        description: 'No K8s domains were found with the current filters.'
      },
      pagination: {
        showing: 'Showing {from} to {to} of {total} domains'
      }
    }
  },
  hosting: {
    title: 'Web Hosting',
    subtitle: 'Manage all hosting sites across every account',
    searchPlaceholder: 'Search by name, domain, or account...',
    status: {
      all: 'All',
      provisioning: 'Provisioning',
      active: 'Active',
      failed: 'Failed',
      suspended: 'Suspended',
      deprovisioned: 'Deprovisioned'
    },
    legacy: {
      all: 'All sites',
      migrated: 'Migrated',
      fresh: 'Newly created'
    },
    table: {
      name: 'Name',
      status: 'Status',
      account: 'Account',
      product: 'Package',
      quota: 'Quota',
      domain: 'Primary domain',
      created: 'Created'
    },
    empty: {
      title: 'No sites found',
      description: 'No hosting sites matched the current filters.'
    },
    pagination: {
      showing: 'Showing {from} to {to} of {total} sites'
    },
    detail: {
      title: 'Site detail',
      subtitle: 'Admin view of a single hosting site',
      notFound: 'Site not found',
      k8sStatus: 'Kubernetes status',
      noK8sStatus: 'No live data available.',
      noDomains: 'No domains linked.',
      noDatabases: 'No databases configured.',
      tabs: {
        overview: 'Overview',
        domains: 'Domains',
        databases: 'Databases',
        actions: 'Actions'
      },
      fields: {
        owner: 'Owner',
        product: 'Package',
        runtime: 'Runtime',
        quota: 'Quota',
        sftp: 'SFTP user',
        created: 'Created',
        trialEnd: 'Trial ends'
      },
      actions: {
        impersonate: 'Impersonate owner',
        restart: 'Restart pod',
        restartDesc: 'Rolls out the deployment (~2s downtime).',
        reconcile: 'Reconcile Site CR',
        reconcileDesc: 'Writes the DB spec back into the cluster.',
        rotateSftp: 'Rotate SFTP password',
        rotateSftpDesc: 'Generates a new password and shows it once.',
        newPassword: 'New password',
        delete: 'Deprovision site',
        deleteDesc: 'Removes the Site CR and marks the site as deprovisioned. Data is kept.',
        deleteConfirmTitle: 'Really deprovision this site?',
        deleteConfirmBody: 'Type the site name "{name}" to confirm.'
      },
      notify: {
        restartSuccess: 'Restart triggered',
        reconcileSuccess: 'Reconcile triggered',
        rotateSuccess: 'SFTP password rotated',
        deleteSuccess: 'Site deprovisioned'
      }
    }
  },
  accountSplit: {
    title: 'Account Splits',
    subtitle: 'Admin-driven bulk transfer of assets between accounts',
    status: {
      draft: 'Draft',
      dry_run_ok: 'Dry-run OK',
      executing: 'Executing',
      partial_failure: 'Partial Failure',
      completed: 'Completed',
      aborted: 'Aborted'
    },
    assetType: {
      subscription: 'Subscription',
      domain: 'Domain',
      handle: 'Handle',
      hosting_site: 'Hosting Site',
      mailbox: 'Mailbox'
    },
    itemStatus: {
      pending: 'Pending',
      in_progress: 'In Progress',
      succeeded: 'Succeeded',
      failed: 'Failed',
      skipped: 'Skipped'
    },
    targetMode: {
      existing_account: 'Existing Account',
      new_account: 'New Account'
    },
    actions: {
      create: 'New Split',
      dryRun: 'Run Dry-run',
      execute: 'Execute',
      resume: 'Resume',
      abort: 'Abort',
      save: 'Save Draft',
      cancel: 'Cancel',
      addItem: 'Add Asset',
      removeItem: 'Remove',
      open: 'Open',
      back: 'Back',
      reopenDryRun: 'Re-open Dry-run',
      confirmExecute: 'Confirm & Execute'
    },
    confirm: {
      execute: 'Execute this split? Assets will be moved to the target account.',
      abort: 'Abort this split? No automated rollback is performed.',
      dryRunRequired: 'A successful dry-run is required before executing.',
      abortReason: 'Reason (optional)',
      abortReasonPlaceholder: 'Why is this split being aborted?'
    },
    dryRun: {
      title: 'Dry-run Result',
      vatDelta: 'VAT delta',
      warnings: 'Warnings',
      plannedActions: 'Planned Actions',
      openInvoices: 'Open Invoices',
      beforeTotal: 'Current total',
      afterTotal: 'After split',
      noChanges: 'No open invoices affected.',
      noItems: 'No items planned for this split.',
      noWarnings: 'No warnings.'
    },
    builder: {
      sourceLabel: 'Source Account',
      targetLabel: 'Target Account',
      assetPicker: 'Select Assets',
      draftSaved: 'Draft saved',
      selectAccount: 'Select an account',
      searchPlaceholder: 'Search by email, name or org',
      noResults: 'No accounts match your search.',
      clearSelection: 'Clear selection',
      assetSubscriptions: 'Subscriptions',
      assetDomains: 'Domains',
      assetSites: 'Hosting Sites',
      assetMailboxes: 'Mailboxes',
      assetHandles: 'Handles',
      noAssets: 'No assets of this type found for the source account.',
      notImplemented: 'Mailboxes can be added via the API after creating the split — UI selector not yet implemented.',
      newAccountIntro: 'Create a new account during execution. Stripe customer and DB row are provisioned on first Execute.',
      notesLabel: 'Operator notes',
      notesPlaceholder: 'Why is this split being created?',
      pickSourceFirst: 'Pick a source account first.',
      saveBeforeDryRun: 'Saving draft before running dry-run...',
      newAccountForm: {
        type: 'Type',
        typePerson: 'Person',
        typeOrganization: 'Organization',
        language: 'Language',
        email: 'Email',
        firstName: 'First name',
        lastName: 'Last name',
        organization: 'Organization',
        line1: 'Street (Line 1)',
        line2: 'Line 2',
        postcode: 'Postcode',
        city: 'City',
        countryIso: 'Country (ISO)',
        phone: 'Phone'
      }
    },
    home: {
      columnId: 'ID',
      columnSource: 'Source',
      columnTarget: 'Target',
      columnStatus: 'Status',
      columnCreated: 'Created',
      columnActions: '',
      emptyTitle: 'No splits yet',
      emptyHint: 'Create one to start splitting an account.'
    },
    detail: {
      sectionMeta: 'Split Metadata',
      sectionItems: 'Items',
      sourceAccount: 'Source Account',
      targetAccount: 'Target Account',
      createdAt: 'Created',
      updatedAt: 'Updated',
      executedAt: 'Executed',
      notes: 'Notes',
      newAccountPayload: 'New account payload',
      failureReason: 'Failure reason',
      columnId: 'ID',
      columnType: 'Asset Type',
      columnAssetId: 'Asset ID',
      columnAttempts: 'Attempts',
      columnError: 'Error',
      columnStarted: 'Started',
      columnFinished: 'Finished',
      noItems: 'No items in this split.',
      beforeSnapshot: 'Before snapshot',
      afterSnapshot: 'After snapshot',
      pollingNotice: 'Execution in progress — polling every 3s.'
    },
    notify: {
      created: 'Split created',
      draftSaved: 'Draft saved',
      itemAdded: 'Asset added',
      itemRemoved: 'Asset removed',
      dryRunSuccess: 'Dry-run completed',
      executeStarted: 'Execution started',
      executeCompleted: 'Split completed successfully',
      executePartial: 'Split partially completed — some assets could not be transferred',
      resumeStarted: 'Resume started',
      aborted: 'Split aborted'
    },
    errors: {
      notFound: 'Split not found',
      notDraftable: 'Split is no longer editable',
      paidInvoiceUnsupported: 'Paid invoices cannot be split in v1 — detach manually first',
      busyOrWrongState: 'Split is busy or in the wrong state for this action',
      validation: 'Invalid request',
      generic: 'An unexpected error occurred'
    }
  },
  roles: {
    title: 'Roles',
    subtitle: 'Manage system roles and permissions',
    loading: 'Loading roles...',
    empty: 'No roles defined',
    emptyDescription: 'No roles are configured in the system.',
    columns: {
      name: 'Name',
      displayName: 'Display Name',
      description: 'Description',
      priority: 'Priority',
      permissions: 'Permissions',
      system: 'System'
    },
    systemBadge: 'System Role',
    permissionCount: '{count} permission | {count} permissions',
    expandPermissions: 'Show permissions',
    collapsePermissions: 'Hide permissions',
    userRoles: {
      title: 'User Roles',
      subtitle: 'Roles for this user',
      noRoles: 'No roles assigned',
      noRolesDescription: 'This user has no special roles.',
      assignRole: 'Assign Role',
      revokeRole: 'Revoke Role',
      grantedAt: 'Granted',
      expiresAt: 'Expires',
      never: 'Never',
      selectRole: 'Select role',
      expiryDate: 'Expiry date',
      noExpiry: 'No expiry date',
      confirmRevoke: 'Are you sure you want to revoke role "{role}"?',
      notifications: {
        assigned: 'Role assigned',
        assignedMessage: '{role} has been assigned to the user.',
        revoked: 'Role revoked',
        revokedMessage: '{role} has been revoked from the user.'
      }
    }
  }
} as const
